/**
 * RibbonDropdown — split-button dropdown (Faz 3.1): the face runs the
 * default binding, the chevron opens the Chakra menu. Menu width follows
 * the widest row (w-max rule); rows are i18n-labeled and run their own
 * binding through the shared executor.
 */

import { Box, Icon, Menu, MenuButton, MenuItem, MenuList, Text, Tooltip } from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import type { RibbonDropdownDef, RibbonEntryDef } from '../../../config/ribbonTabs';
import { runRibbonBinding, bindingTool } from './ribbonCommand';
import { KeytipBadge, useEntryTooltip, useItemLabel } from './KeytipBadge';
import { useAppStore } from '../../../state/store';
import { usePanelMemoryStore } from './panelMemoryStore';

/** One row inside a ribbon dropdown. */
export function RibbonMenuRow({ entry, onClose }: { entry: RibbonEntryDef; onClose?: () => void }) {
    const label = useItemLabel(entry.key);
    const IconCmp = entry.icon;
    const tool = bindingTool(entry);
    const activeTool = useAppStore((s) => s.activeTool);

    return (
        <MenuItem
            icon={IconCmp ? <Icon as={IconCmp} boxSize="14px" color="icon.base" /> : undefined}
            onClick={() => {
                runRibbonBinding(entry);
                onClose?.();
            }}
            bg={tool && activeTool === tool ? 'bg.active' : undefined}
            fontSize="12px"
            color="text.primary"
        >
            {label}
        </MenuItem>
    );
}

interface RibbonDropdownProps {
    entry: RibbonDropdownDef;
    groupKey: string;
    keytipsOn: boolean;
}

export function RibbonDropdown({ entry, groupKey, keytipsOn }: RibbonDropdownProps) {
    const label = useItemLabel(entry.key);
    const tooltip = useEntryTooltip(entry);
    const IconCmp = entry.icon;
    const record = usePanelMemoryStore((s) => s.record);
    const activeTool = useAppStore((s) => s.activeTool);
    const large = entry.kind === 'largeDropdown';

    const hasDefault = !!(entry.cmdId || entry.shell || entry.tool);
    const faceTool = bindingTool(entry);
    const isActive = !!faceTool && activeTool === faceTool;

    if (!IconCmp) return null;

    const runDefault = () => {
        if (!hasDefault) return;
        runRibbonBinding(entry);
        record(groupKey, entry.key);
    };

    return (
        <Menu closeOnSelect autoSelect={false}>
            <Box position="relative" display="flex" alignItems="stretch" flexShrink={0}>
                <Tooltip label={tooltip} placement="bottom" openDelay={400} hasArrow>
                    <Box
                        as="button"
                        role="button"
                        tabIndex={0}
                        aria-label={label}
                        onClick={runDefault}
                        display="flex"
                        flexDirection={large ? 'column' : 'row'}
                        alignItems="center"
                        justifyContent="center"
                        gap={large ? '2px' : 0}
                        w={large ? '72px' : '26px'}
                        h={large ? 'full' : '26px'}
                        px={1}
                        borderRadius="4px"
                        bg={isActive ? 'bg.active' : 'transparent'}
                        _hover={{ bg: isActive ? 'bg.active' : 'bg.hover' }}
                    >
                        <Icon as={IconCmp} boxSize={large ? '22px' : '15px'} color="icon.base" />
                        {large && (
                            <Text fontSize="10px" lineHeight="12px" color="text.primary" noOfLines={1} maxW="68px">
                                {label}
                            </Text>
                        )}
                    </Box>
                </Tooltip>
                <MenuButton
                    as={Box}
                    role="button"
                    aria-label={`${label} menu`}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="12px"
                    borderRadius="4px"
                    _hover={{ bg: 'bg.hover' }}
                    _expanded={{ bg: 'bg.active' }}
                >
                    <Icon as={ChevronDown} boxSize="11px" color="text.secondary" />
                </MenuButton>
                <KeytipBadge letter={entry.keytip} show={keytipsOn} />
            </Box>
            <MenuList w="max-content" minH="0" bg="bg.panel" borderColor="border.base">
                {entry.items.map((item) => (
                    <RibbonMenuRow key={item.key} entry={item} />
                ))}
            </MenuList>
        </Menu>
    );
}
